<template>
  <div class="registry-device-selector">
    <MultiSelectComponent
      :options="availableOptions"
      :modelValue="model"
      @update:modelValue="handleSelectionChange"
      :placeholder="placeholderComputed"
      selectedFirst
    >
      <template #option="{ option }">
        <div class="d-flex align-items-center">
          <TechnologyIcon
            :technology="
              getDeviceTechnology(
                option.value.toString(),
                registryDevices ?? []
              )
            "
            iconClass="me-2"
          />
          <div>
            <div class="fw-medium">{{ option.label }}</div>
            <div class="text-muted small" v-if="option.description">
              {{ option.description }}
            </div>
          </div>
        </div>
      </template>
    </MultiSelectComponent>

    <div v-if="hasValidationErrors" class="text-danger small mt-2">
      <FontAwesomeIcon icon="exclamation-triangle" class="me-1" />
      <span
        v-for="(message, deviceId) in validationErrors"
        :key="deviceId"
        v-show="message"
      >
        {{ message }}
      </span>
    </div>

    <div v-if="model.length > 0" class="registry-device-selector__chosen">
      <div class="registry-device-selector__summary">
        <span class="eyebrow-label">
          <FontAwesomeIcon
            icon="circle-check"
            class="registry-device-selector__check"
          />
          {{
            $t("portfolio_instructions.selected_devices_summary", {
              count: model.length,
            })
          }}
        </span>
        <button
          type="button"
          class="btn btn-link btn-sm registry-device-selector__clear"
          @click="clearSelection"
        >
          {{ $t("common.clear_all") }}
        </button>
      </div>
      <div class="registry-device-selector__list">
        <span
          v-for="deviceId in model"
          :key="deviceId"
          class="registry-device-selector__chip"
        >
          <TechnologyIcon
            :technology="getDeviceTechnology(deviceId, registryDevices ?? [])"
            iconClass="me-1"
          />
          {{ getDeviceName(deviceId, registryDevices ?? []) }}
          <button
            type="button"
            class="registry-device-selector__chip-remove"
            :aria-label="
              $t('portfolio_instructions.remove_device', {
                deviceName: getDeviceName(deviceId, registryDevices ?? []),
              })
            "
            @click="removeDevice(deviceId)"
          >
            <FontAwesomeIcon icon="xmark" />
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import TechnologyIcon from "@/components/common/TechnologyIcon.vue";
import {
  getDeviceName,
  getDeviceTechnology,
} from "@/dashboard/production_strategy/overview/PPA/ppaDeviceUtils";
import type { RegistryDevice } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface DeviceOption {
  label: string;
  value: string | number;
  description?: string;
}

const props = defineProps<{
  registryDevices: RegistryDevice[];
  availableOptions: DeviceOption[];
  deviceValidationErrors?: Record<string, string>;
  placeholder?: string;
}>();

const model = defineModel<string[]>({ required: true });

const { t } = useI18n();

const validationErrors = computed<Record<string, string>>(
  () => props.deviceValidationErrors ?? {}
);
const hasValidationErrors = computed(() =>
  Object.values(validationErrors.value).some((message) => Boolean(message))
);
const placeholderComputed = computed(
  () => props.placeholder ?? t("portfolio_instructions.select_devices")
);

const handleSelectionChange = (deviceIds: (string | number)[]) => {
  model.value = deviceIds.map((id) => id.toString());
};

const clearSelection = () => {
  if (model.value.length === 0) {
    return;
  }
  model.value = [];
};

const removeDevice = (deviceId: string) => {
  model.value = model.value.filter((id) => id !== deviceId);
};

defineExpose({
  focus: () => undefined,
});
</script>

<style scoped lang="scss">
.registry-device-selector__chosen {
  margin-top: 12px;
}

.registry-device-selector__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.registry-device-selector__check {
  color: var(--phoenix-success);
}

.registry-device-selector__clear {
  padding: 2px 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--phoenix-primary);
  text-decoration: none;

  &:hover {
    background: var(--phoenix-primary-bg-subtle);
    border-radius: 6px;
    color: var(--phoenix-primary);
  }
}

.registry-device-selector__list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border: 1px dashed var(--phoenix-border-color);
  border-radius: 8px;
  background: var(--phoenix-gray-50);
  min-height: 44px;
}

.registry-device-selector__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px 5px 10px;
  border-radius: 999px;
  background: var(--phoenix-primary-bg-subtle);
  color: var(--phoenix-primary-text-emphasis);
  font-size: 12px;
  font-weight: 600;
  border: 1px solid rgba(var(--phoenix-primary-rgb), 0.18);
}

.registry-device-selector__chip-remove {
  all: unset;
  cursor: pointer;
  display: inline-flex;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  align-items: center;
  justify-content: center;
  color: var(--phoenix-primary-text-emphasis);
  opacity: 0.7;
  font-size: 10px;

  &:hover {
    background: rgba(var(--phoenix-primary-rgb), 0.15);
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: 2px;
    background: rgba(var(--phoenix-primary-rgb), 0.15);
    opacity: 1;
  }
}
</style>
